import {
  filter,
  find,
  get,
  groupBy,
  isEmpty,
  isUndefined,
  map,
  omit,
  times,
  uniqBy,
} from 'lodash';
import {
  getNonEmptyQl,
  getRuleEntries,
} from 'containers/QuestionnaireLogic/utils';
import { QUESTION_NEST_PEEQ } from 'data/nodeTypes';
import { QUESTIONNAIRE_REVISION_REASONS } from 'components/QuestionnaireRevisions/constants';
import {
  AGE_CRITERION_ID,
  usFinancialOptionGroups,
} from 'containers/TargetingPeeqPage/constants';
import { AI_REPORT_SOURCE_ENUM } from './constants';

export function getSurveyStoreParams({
  questionnaireData,
  targetingData,
  surveyId,
  isThirdParty,
  isDistributionLink,
  isPeeq,
  saveCheckpoint = false,
  saveReason = QUESTIONNAIRE_REVISION_REASONS.AUTO_SAVE,
}) {
  const {
    questions,
    questionGroups,
    costAffected: costAffectedByQuestionnaire = false,
    logic,
    ql,
    disqualificationQl = false,
    version: questionnaireEditVersion,
    questionGroupShuffle,
    hiddenVariables,
    quotaMinimalMap,
  } = questionnaireData;

  const {
    targeting = {},
    previousVersion,
    version: targetingEditVersion,
    feasibilityAffected = false,
    costAffected: costAffectedByTargeting = false,
    isRevision = false,
  } = targetingData;

  const costAffected = costAffectedByTargeting || costAffectedByQuestionnaire;

  const { audiences, screeningQuestions, countryId } = targeting;

  const targetingObject = {
    audiences,
    screeningQuestions,
    countryId,
    previousTargetingEditVersion: previousVersion,
    targetingEditVersion,
    isRevision,
  };

  const qlToStore = getNonEmptyQl(ql);
  if (qlToStore) {
    qlToStore.questionnaire.surveyId = get(
      qlToStore,
      'questionnaire.surveyId',
      surveyId,
    );
  }

  const questionnaireObject = {
    questions,
    questionGroups,
    logic,
    ql: qlToStore,
    ruleEntries: getRuleEntries(ql, quotaMinimalMap, questions),
    questionnaireEditVersion,
    questionGroupShuffle,
    hiddenVariables: getHiddenVariablesForAPI(hiddenVariables),
  };

  if (disqualificationQl) {
    questionnaireObject.disqualificationRules = {
      json: disqualificationQl.ql,
      disqualificationRulesEditVersion: disqualificationQl.version,
    };
  }

  const params = {
    surveyId,
    questionnaire: questionnaireObject,
    isThirdParty,
    isDistributionLink,
    isPeeq,
  };

  if (!isDistributionLink) {
    Object.assign(params, { targeting: targetingObject });
  }

  if (saveCheckpoint) {
    params.saveCheckpoint = true;
    params.saveReason = saveReason;
  }

  return {
    params,
    feasibilityAffected,
    costAffected,
    questionnaireEditVersion,
    targetingEditVersion,
  };
}

export function prepareAudienceFeatureForAPI(feature = {}) {
  if (feature.style === QUESTION_NEST_PEEQ) {
    return prepareAudienceFeatureNestingForAPI(feature);
  }
  return prepareAudienceFeatureGroupAnswersForAPI(feature);
}

export function prepareAudienceFeatureNestingForAPI({
  features,
  relations,
  hasQuotas,
}) {
  const groups = features.reduce((accumulator, { key, values }) => {
    filter(
      values,
      (filterValue) => filterValue.isGroup || isOptionGroup(filterValue),
    ).forEach((value) => {
      accumulator.push({
        key,
        refId: value.refId,
        name: value.groupName,
        values: map(value.values, 'value'),
      });
    });

    return accumulator;
  }, []);

  const featuresByKey = groupBy(features, 'key');
  const nestedValues = relations.map((relation) => {
    const newValue = {
      options: [],
      quotaCompletes: relation.quotaCompletes,
      refId: relation.refId,
    };
    relation.features.forEach(({ key, featureValueRefId }) => {
      const { excludeOptions, values } = featuresByKey[key][0];
      const option = { key, excludeOptions };

      const foundValue = find(values, ['refId', featureValueRefId]);
      if (foundValue.isGroup || isOptionGroup(foundValue)) {
        foundValue.values.forEach(({ value }) => {
          newValue.options.push({ ...option, value });
        });
      } else {
        newValue.options.push({
          ...option,
          value: foundValue.value,
          label: foundValue.label,
        });
      }
    });

    return newValue;
  });

  const nestCriterion = {
    keys: map(features, 'key'),
    groups: map(groups, (group) => omit(group, 'refId')),
    nestedValues,
    hasQuotas,
  };

  // *** Start Code To Handle Age Groups ***
  if (nestCriterion.keys.includes(AGE_CRITERION_ID.toString())) {
    const ageGroups = [];
    nestCriterion.nestedValues = nestCriterion.nestedValues.map(
      (nestedValue) => {
        const nonAgeOptions = [];
        let ageOption = {};

        nestedValue.options.forEach((option) => {
          if (option.key === AGE_CRITERION_ID.toString()) {
            ageOption = option;
          } else {
            nonAgeOptions.push(option);
          }
        });

        const values = prepareFeatureAgeGroupValuesForAPI(ageOption.value);
        ageGroups.push({
          key: ageOption.key,
          name: ageOption.value,
          values,
        });

        return {
          ...nestedValue,
          options: [
            ...nonAgeOptions,
            ...values.map((value) => ({ ...ageOption, value })),
          ],
        };
      },
    );
    nestCriterion.groups = [
      ...nestCriterion.groups,
      ...uniqBy(ageGroups, 'name'),
    ];
  }
  // *** End Code To Handle Age Groups ***
  return nestCriterion;
}

export function isOptionGroup(group) {
  if (!group?.values) return false;

  const groupValue = group.value;
  const groupOptionValues = group.values.map((item) => item.value);
  const groupName = group.groupName || group.text;

  return usFinancialOptionGroups.some((optionGroup) => {
    const nameCheck = optionGroup.text === groupName;
    const valueCheck = optionGroup.value === groupValue;
    const optionsCheck =
      optionGroup.optionValues.length === groupOptionValues.length &&
      optionGroup.optionValues.every((value) =>
        groupOptionValues.includes(value),
      );

    return nameCheck && valueCheck && optionsCheck;
  });
}

function prepareAudienceFeatureGroupAnswersForAPI(feature) {
  const grouped = groupBy(
    feature.values,
    (value) => value.isGroup || isOptionGroup(value),
  );

  const inGroups = grouped.true || [];
  const notInGroups = grouped.false || [];

  if (isEmpty(inGroups)) {
    return feature.key === AGE_CRITERION_ID.toString()
      ? prepareFeatureAgeForAPI(feature)
      : feature;
  }

  const extractedGroupValues = [];
  const groups = inGroups.map((group) => {
    extractedGroupValues.push(...group.values);
    const newGroup = {
      name: group.groupName,
      values: map(group.values, 'value'),
    };

    if (!isUndefined(group.quotaCompletes)) {
      newGroup.quotaCompletes = group.quotaCompletes;
    }
    return newGroup;
  });

  return {
    ...feature,
    groups,
    values: uniqBy([...notInGroups, ...extractedGroupValues], 'value'),
  };
}

export const prepareHiddenVariablesForAPI = (hiddenVariables = []) =>
  hiddenVariables.map((hiddenVariable) => {
    const { isNew, ...restProps } = hiddenVariable;
    return {
      ...restProps,
      values: restProps.values.map((value) => {
        const { isNew: isNewValue, ...restPropsValue } = value;
        return restPropsValue;
      }),
    };
  });

export function prepareFeatureAgeGroupValuesForAPI(value) {
  const min = parseInt(value.split('-')[0], 10);
  const max = parseInt(value.split('-')[1], 10);

  return times(max - min + 1, (i) => (min + i).toString());
}

export function prepareFeatureAgeForAPI(feature) {
  const groups = feature.values.map((group) => {
    const newGroup = {
      name: group.value,
      values: prepareFeatureAgeGroupValuesForAPI(group.value),
    };

    if (!isUndefined(group.quotaCompletes)) {
      newGroup.quotaCompletes = group.quotaCompletes;
    }
    return newGroup;
  });

  return {
    ...feature,
    groups,
    values: feature.values.map((value) => omit(value, ['quotaCompletes'])),
  };
}

export function getHiddenVariablesForAPI(hiddenVariables = []) {
  if (isEmpty(hiddenVariables)) {
    return undefined;
  }

  return prepareHiddenVariablesForAPI(hiddenVariables);
}

export function getAiReportUrl({
  surveyId,
  userId,
  source = AI_REPORT_SOURCE_ENUM.MY_SURVEYS,
}) {
  return `https://pollfish.com/dashboard/results/${surveyId}/report?vid=${userId}&source=${source}&land=mainAIreport`;
}
